import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import styles from './Section.module.css';

/** Underlined text link inside marketing copy (hero sign-in, FAQ privacy link). */
export const MARKETING_INLINE_LINK_CLASS =
  // min-h-11: a 44px tap target even inline (WCAG 2.5.8 and the phone 44px rule).
  styles.marketingInlineLink;

export type SectionTone = 'light' | 'tint' | 'dark' | 'hero';

const TONE_CLASS: Record<SectionTone, string | undefined> = {
  light: styles.toneLight,
  tint: styles.toneTint,
  dark: cn(styles.toneLight, 'marketing-invert'),
  hero: cn(styles.toneLight, 'marketing-hero-wash'),
};

interface SectionProps {
  id?: string;
  /** Background treatment. Alternating tones are what give the page its rhythm. */
  tone?: SectionTone;
  /** `id` of the heading that names this section. */
  labelledBy?: string;
  children: ReactNode;
  className?: string;
  /** Overrides the inner column, e.g. a narrower measure for FAQ copy. */
  containerClassName?: string;
}

/**
 * Full-bleed band with a centred column inside.
 *
 * The platform stacks sections in one measured column separated by hairlines,
 * which reads as documentation. A sales page instead needs each section to own
 * the full viewport width so the background can change between them — that
 * colour change, not a border, is what separates one pitch from the next.
 */
export function Section({
  id,
  tone = 'light',
  labelledBy,
  children,
  className,
  containerClassName,
}: SectionProps) {
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      className={cn(
        TONE_CLASS[tone],
        // Clears the sticky header, which is two rows tall below lg.
        id && styles.section,
        styles.section2,
        className,
      )}
    >
      <div className={cn(styles.box, containerClassName)}>{children}</div>
    </section>
  );
}

interface SectionHeadingProps {
  id: string;
  eyebrow?: string;
  title: string;
  description?: string;
  /** Centre the block. Left-aligned by default, which suits copy-heavy sections. */
  align?: 'left' | 'center';
  className?: string;
}

/** Eyebrow + h2 + lead paragraph, at sales-page scale rather than tool scale. */
export function SectionHeading({
  id,
  eyebrow,
  title,
  description,
  align = 'left',
  className,
}: SectionHeadingProps) {
  const centered = align === 'center';

  return (
    <div className={cn(centered && styles.box2, className)}>
      {eyebrow ? (
        <p className={styles.text}>
          {eyebrow}
        </p>
      ) : null}
      <h2
        id={id}
        className={cn(
          styles.heading,
          eyebrow && styles.heading2,
        )}
      >
        {title}
      </h2>
      {description ? (
        <p
          className={cn(
            styles.text2,
            centered ? styles.text3 : styles.text4,
          )}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}
